import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type {
  FeedbackRecord,
  FeedbackSubjectType,
  FeedbackDataType,
} from '@erp/rpc';

export interface SubmitFeedbackInput {
  subjectType: FeedbackSubjectType;
  subjectId: string;
  scoreName: string;
  value: {
    dataType: FeedbackDataType;
    numericValue?: number | null;
    categoricalValue?: string | null;
    booleanValue?: boolean | null;
  };
  comment?: string | null;
  traceId?: string | null;
  observationId?: string | null;
}

export function useSubmitFeedback() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<{ feedback: FeedbackRecord }, Error, SubmitFeedbackInput>({
    mutationFn: async (input) => {
      return await rpcClient.core.userFeedback.submit(input);
    },
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.core.userFeedback.getMyForSubject.key({
          input: {
            subjectType: input.subjectType,
            subjectId: input.subjectId,
          },
        }),
      });
      showToast({
        title: 'Feedback saved',
        message: 'Thanks for your feedback.',
        color: 'success',
        icon: 'check',
      });
    },
    onError: (error) => {
      showToast({
        title: 'Could not save feedback',
        message: error.message,
        color: 'error',
        icon: 'x',
      });
    },
  });
}
